import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ClientSubdomainState } from "../objects/clientSubdomainState";
import { EMPTY_DOMAIN_CRITERIA } from "../constants/criteria";
import { Domain } from "../objects/domain";
import { DomainCriteria } from "../objects/domainCriteria";
import { ErrorKeys } from "../../../functions/errorKeys";
import { ClientSubdomainEditStep } from "../objects/clientSubdomainEditStep";
import { DomainKey } from "../objects/domainKey";
import { ListCriteria } from "../objects/listCriteria";
import { searchOnArray, sortArray } from "../utils/filterArray";

const initialState: ClientSubdomainState = {
    client_domain_list: [],
    client_domain_count: 0,
    client_domain_keys: [],
    client_domain_keys_sorted: [],
    client_domain_keys_criteria: { order_by: 'identifier', search: '', order: 'asc' },
    client_domain_list_loading: false,
    client_domain_list_criteria: EMPTY_DOMAIN_CRITERIA,
    client_domain: null,
    domain: '',
    domain_error: '',
    filter_open: false,
    refresh: false,
    edit_step: ClientSubdomainEditStep.INFO,
    edit_info: false
}
const refreshSubdomainKeysSortedListReducer = (state: ClientSubdomainState): ClientSubdomainState => ({
    ...state,
    client_domain_keys_sorted: sortArray(
        searchOnArray(
            state.client_domain_keys,
            state.client_domain_keys_criteria.search || null,
        ),
        state.client_domain_keys_criteria.order || 'asc',
        state.client_domain_keys_criteria.order_by || '',
    )
});
const resetClientDomainReducer = (state: ClientSubdomainState): ClientSubdomainState => ({
    ...state,
    domain: state.client_domain?.domain || '',
    domain_error: '',
    edit_step: ClientSubdomainEditStep.INFO
});
const setEditStepReducer = (state: ClientSubdomainState, action: PayloadAction<ClientSubdomainEditStep>): ClientSubdomainState => ({
    ...state,
    edit_step: action.payload
});
const setErrorReducer = (state: ClientSubdomainState, action: PayloadAction<{ field: ErrorKeys<ClientSubdomainState>, value: string }>): ClientSubdomainState => ({
    ...state, [action.payload.field]: action.payload.value
});
const toggleEditInfoReducer = (state: ClientSubdomainState): ClientSubdomainState => ({
    ...state, edit_info: !state.edit_info
});
const toggleFilterReducer = (state: ClientSubdomainState): ClientSubdomainState => ({
    ...state, filter_open: !state.filter_open
});
const toggleRefreshReducer = (state: ClientSubdomainState): ClientSubdomainState => ({
    ...state, refresh: !state.refresh
});
const updateClientDomainCountReducer = (state: ClientSubdomainState, action: PayloadAction<number>): ClientSubdomainState => ({
    ...state, client_domain_count: action.payload
});
const updateClientDomainKeysReducer = (state: ClientSubdomainState, action: PayloadAction<DomainKey[]>): ClientSubdomainState => ({
    ...state, client_domain_keys: action.payload
});
const updateClientDomainKeysCriteriaReducer = (state: ClientSubdomainState, action: PayloadAction<ListCriteria<DomainKey>>): ClientSubdomainState => ({
    ...state, client_domain_keys_criteria: action.payload
})
const updateClientDomainListReducer = (state: ClientSubdomainState, action: PayloadAction<Domain[]>): ClientSubdomainState => ({
    ...state, client_domain_list: action.payload, client_domain_list_loading: false
});
const updateClientDomainListCriteriaReducer = (state: ClientSubdomainState, action: PayloadAction<DomainCriteria>): ClientSubdomainState => ({
    ...state, client_domain_list_criteria: action.payload
});
const updateClientDomainListLoadingReducer = (state: ClientSubdomainState, action: PayloadAction<boolean>): ClientSubdomainState => ({
    ...state, client_domain_list_loading: action.payload
});
const updateClientDomainReducer = (state: ClientSubdomainState, action: PayloadAction<Domain | null>): ClientSubdomainState => ({
    ...state,
    client_domain: action.payload,
    domain: action.payload?.domain || '',
    domain_error: ''
})
const updateDomainReducer = (state: ClientSubdomainState, action: PayloadAction<string>): ClientSubdomainState => ({
    ...state,
    domain: action.payload,
    domain_error: ''
})

export const clientSubdomain = createSlice({
    name: 'clientSubdomain',
    initialState,
    reducers: {
        refreshSubdomainKeysSortedList: refreshSubdomainKeysSortedListReducer,
        resetClientDomain: resetClientDomainReducer,
        setEditStep: setEditStepReducer,
        setError: setErrorReducer,
        toggleEditInfo: toggleEditInfoReducer,
        toggleFilter: toggleFilterReducer,
        toggleRefresh: toggleRefreshReducer,
        updateClientDomainCount: updateClientDomainCountReducer,
        updateClientDomainKeys: updateClientDomainKeysReducer,
        updateClientDomainKeysCriteria: updateClientDomainKeysCriteriaReducer,
        updateClientDomainList: updateClientDomainListReducer,
        updateClientDomainListCriteria: updateClientDomainListCriteriaReducer,
        updateClientDomainListLoading: updateClientDomainListLoadingReducer,
        updateClientDomain: updateClientDomainReducer,
        updateDomain: updateDomainReducer,
    }
})

export const {
    refreshSubdomainKeysSortedList,
    resetClientDomain,
    setEditStep,
    setError,
    toggleEditInfo,
    toggleFilter,
    toggleRefresh,
    updateClientDomainCount,
    updateClientDomainKeys,
    updateClientDomainKeysCriteria,
    updateClientDomainList,
    updateClientDomainListCriteria,
    updateClientDomainListLoading,
    updateClientDomain,
    updateDomain
} = clientSubdomain.actions